import { Component, ElementRef, Injector, OnInit, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { EmpenhoService, FichaDespesaService } from 'administrativo-lib';
import {
  BaseResourceFormComponent, DateFormatPipe, EddyAutoComplete,
  Empenho, FuncaoService, GlobalService, LoginContabil, Mensagem, OrgaoAssinaturaService, PreLiquidacao
} from 'eddydata-lib';
import { ConfirmationService, MessageService } from 'primeng/api';
import { takeUntil } from 'rxjs/operators';
import { NotaLiquidacao } from '../../relatorio/liquidacao/nota-liquidacao';
import { PreLiquidacaoService } from '../service/pre-liquidacao.service';

@Component({
  selector: 'lib-pre-liquidacao-form',
  templateUrl: './pre-liquidacao-form.component.html'
})
export class PreLiquidacaoFormComponent extends BaseResourceFormComponent<PreLiquidacao, LoginContabil> implements OnInit {

  /**
   * Declaração de variáveis
   */
  @ViewChild('numero_empenho') autoComplete: ElementRef;

  public especie: string;
  public listaUfs: Array<any>;
  public empenhoAutoComplete: EddyAutoComplete<Empenho>;
  public empenhado: number;
  public liquidado: number;
  public pago: number;
  public redirecionar = false;
  public empenhoNumero: number;
  public listaTipoDocumentos: Array<any>;

  /**
   * Construtor com as injeções de dependencias
   */
  constructor(
    protected injector: Injector,
    protected confirmationService: ConfirmationService,
    protected globalService: GlobalService,
    protected funcaoService: FuncaoService,
    protected messageService: MessageService,
    protected fichaService: FichaDespesaService,
    protected empenhoService: EmpenhoService,
    protected assinaturaService: OrgaoAssinaturaService,
    protected preliquidacaoService: PreLiquidacaoService) {
    super(new PreLiquidacao(), injector, PreLiquidacao.converteJson, preliquidacaoService);
  }

  // ========================================================================
  //                        MÉTODOS ABSTRAÍDOS
  // ========================================================================

  protected criarCamposForm(): void {
    this.entidadeForm = this.fb.group({
      id: [null],
      parcela: [null, [Validators.required]],
      valor_liquidado: [null, [Validators.required]],
      data_liquidacao: [null, [Validators.required]],
      data_vencimento: [null, [Validators.required]],
      data_referencia: [null],
      data_emissao: [null],
      historico: [null, [Validators.required, Validators.minLength(2)]],
      mes: [null, [Validators.required]],
      documento: [null, [Validators.required]],
      tipo_documento: [null, [Validators.required]],
      anulacao: [false, [Validators.required]],
      impresso: [false, [Validators.required]],
      empenho: [null, [Validators.required]],
      serie: [null],
      orgao: [this.login.orgao, [Validators.required]],
      exercicio: [this.login.exercicio, [Validators.required]],
      usuario_cadastro: [this.login.usuario, [Validators.required]]
    });
  }

  protected parametrosExtras(): {} {
    return {
      relations: 'empenho,empenho.modalidade,empenho.ficha,empenho.ficha.acao,empenho.favorecido,empenho.favorecido.tipo,empenho.subelemento,'
        + 'empenho.exercicio,empenho.orgao,empenho.contrato,empenho.contrato.tipo_contratacao,empenho.ficha.despesa,empenho.ficha.funcao,'
        + 'empenho.ficha.subfuncao,empenho.ficha.programa,empenho.ficha.recurso,empenho.ficha.aplicacao,orgao,orgao.cidade,exercicio'
    };
  }

  protected afterInit(): void {
    this.listaUfs = this.globalService.listaEstados();
    this.carregarAutoCompletes();
    this.inicializaVariavel();
    this.listaTipoDocumentos = [
      { nome: 'NOTA FISCAL', id: '1' },
      { nome: 'RECIBO', id: '2' },
      { nome: 'OUTRO', id: '3' }
    ];
  }

  protected campoFoco(): ElementRef {
    return this.autoComplete;
  }

  protected afterLoad() {

    this.empenhoNumero = this.entidade.empenho.numero;
    this.entidade.data_liquidacao = new DateFormatPipe().transform(this.entidade.data_liquidacao, []);
    this.entidade.data_vencimento = new DateFormatPipe().transform(this.entidade.data_vencimento, []);
    this.entidade.data_referencia = new DateFormatPipe().transform(this.entidade.data_referencia, []);
    this.entidade.data_emissao = new DateFormatPipe().transform(this.entidade.data_emissao, []);
  }

  protected async beforeSubmit() {
    try {
      if (!this.entidadeForm.get('empenho').value) {
        throw new Error('Informe o empenho para pré-liquidação');
      }
      if (!this.entidadeForm.get('exercicio').value) {
        throw new Error('Informe o exercício da pré-liquidação');
      }
      if (!this.entidadeForm.get('orgao').value) {
        throw new Error('Informe o órgão da pré-liquidação');
      }
      if (!this.entidadeForm.get('historico').value) {
        throw new Error('Informe o histórico da pré-liquidação');
      }
      if (!this.entidadeForm.get('valor_liquidado').value) {
        throw new Error('Informe o valor da pré-liquidação');
      }
      if (!this.entidadeForm.get('data_liquidacao').value) {
        throw new Error('Informe a data da pré-liquidação');
      }
      if (!this.entidadeForm.get('data_vencimento').value) {
        throw new Error('Informe o vencimento da pré-liquidação');
      }
      if (!this.entidadeForm.get('documento').value) {
        throw new Error('Informe o documento da pré-liquidação');
      }

      if (this.entidadeForm.get('valor_liquidado').value <= 0 && !this.entidadeForm.get('anulacao').value) {
        throw new Error('O valor da pré-liquidação deve ser maior que zero');
      }
      const dtLiquidacao: Date = this.entidadeForm.get('data_liquidacao').value;
      if (dtLiquidacao.getFullYear() !== this.login.exercicio.ano) {
        throw new Error('O ano da data da pré-liquidação está diferente do exercício logado');
      }
      if (this.empenhado - this.liquidado + this.entidade.valor_liquidado - this.entidadeForm.get('valor_liquidado').value < 0) {
        throw new Error('Não há saldo para continuar a pré-liquidação, verifique!');
      }
      const validado = await this.validarSimplesNacional();
      if (!validado) {
        throw new Error('Simples Nacional inválido!');
      }

      const validarDocumentoReptido = await this.validarDocumentoRepetido();
      if (!validarDocumentoReptido) {
        throw new Error('Documento repetido!');
      }

      const validarContratoVencido = await this.validarContratoVencido();
      if (!validarContratoVencido) {
        throw new Error('Contrato vencido!');
      }
      this.entidadeForm.get('mes').setValue(dtLiquidacao.getUTCMonth() + 1);
    } catch (e) {
      Mensagem.erro(this.messageService, e)
      throw e;
    }
  }

  private validarSimplesNacional() {
    if (!this.entidade.id && this.entidade.empenho.favorecido.simples_nacional &&
      (this.entidade.empenho.subelemento.codigo.substr(4, 2) === '36' ||
        this.entidade.empenho.subelemento.codigo.substr(4, 2) === '39' ||
        this.entidade.empenho.subelemento.codigo.substr(4, 2) === '51')) {
      return new Promise((resolve, reject) => {
        this.confirmationService.confirm({
          header: 'Atenção',
          message: 'Empresa optante pelo simples nacional, dispensada da retenção de IRRF e INSS desde que apresente declaração.'
            + 'Se a mesma estiver enquadrada no ANEXO IV da LEI 123/06, porém, haverá retenção de INSS. Deseja continuar?',
          accept: () => {
            resolve(true);
          },
          reject: () => {
            resolve(false);
          }
        });
      });
    } else { return true; }
  }

  private async validarDocumentoRepetido() {
    if (!this.entidade.id) {
      const param = {};
      param['empenho.favorecido.id'] = this.entidade.empenho.favorecido.id;
      param['documento'] = this.entidade.documento;
      param['valor_liquidado'] = this.entidade.valor_liquidado;
      param['orgao.id'] = this.entidade.orgao.id;
      param['exercicio.id'] = this.entidade.exercicio.id;
      if (this.entidade.id) {
        param['id$ne'] = this.entidade.id;
      }
      const entity = await this.preliquidacaoService.obter(param).toPromise();
      if (entity) {
        return new Promise((resolve, reject) => {
          this.confirmationService.confirm({
            header: 'Atenção',
            message: 'Já existe liquidação com este fornecedor, documento e valor, deseja continuar ?',
            accept: () => {
              resolve(true);
            },
            reject: () => {
              resolve(false);
            }
          });
        });
      } else { return true; }
    } else { return true; }
  }

  private async validarContratoVencido() {
    if (!this.entidade.id && this.entidade.empenho.contrato
      && this.entidade.data_liquidacao > this.entidade.empenho.contrato.data_termino) {
      return new Promise((resolve, reject) => {
        this.confirmationService.confirm({
          header: 'Atenção',
          message:
            `Empenho ${this.entidade.empenho.numero}/${this.entidade.exercicio.ano} de contrato ${this.entidade.empenho.contrato.numero}
                 vencido em ${new DateFormatPipe().transform(this.entidade.data_liquidacao, ['local'])}. Deseja continuar?`,
          accept: () => {
            resolve(true);
          },
          reject: () => {
            resolve(false);
          }
        });
      });
    } else { return true; }
  }

  protected afterSubmit(ent: PreLiquidacao) {
    if (this.currentActionRoute === 'novo') {
      if (!this.limparTela) {
        this.router.navigate(['/pre-liquidacoes', ent.id, 'editar']);
      }
    } else {
      this.router.navigate(['/pre-liquidacoes', ent.id, 'editar']).then(() => {
      });
    }
  }

  private inicializaVariavel() {
    if (this.currentActionRoute === 'novo') {
      this.preliquidacaoService.ultimaDataLiquidada(this.login.exercicio.id, this.login.orgao.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((dataLiq) => {
          if (dataLiq === 0) {
            this.entidade.data_liquidacao = new Date();
          } else {
            this.entidade.data_liquidacao = new DateFormatPipe().transform(dataLiq, []);
          }
          this.entidadeForm.get('data_liquidacao').setValue(this.entidade.data_liquidacao);
          this.entidadeForm.get('mes').setValue(this.entidade.data_liquidacao.getUTCMonth() + 1);
        });

      this.activatedRoute.params.pipe(takeUntil(this.unsubscribe))
        .subscribe(
          (params: any) => {
            if (params.hasOwnProperty('empenho')) {
              const parametros = {};
              parametros['id'] = params['empenho'];
              parametros['relations'] = 'exercicio,orgao,favorecido,subelemento,ficha,ficha.acao,ficha.despesa,ficha.programa';
              this.empenhoService.obter(parametros)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe(
                  (entidade) => {
                    this.redirecionar = true;
                    this.empenhoNumero = entidade.numero;
                    this.entidadeForm.get('empenho').setValue(entidade);
                    this.entidadeForm.get('historico').setValue(entidade.historico);
                    this.entidadeForm.get('data_vencimento').setValue(new DateFormatPipe().transform(entidade.data_vencimento, []));
                    this.entidade.empenho = entidade;
                    if (this.entidade.empenho) {
                      this.loadTotalizadores();
                      this.obterUltimaParcela();
                    }
                  }, () => this.sair());
            }
          });
    }
  }
  /* Se o usuario informou um valor diferente do empenhado
   * atribui a parcela como sendo a primeira
   */
  public verificarParcela() {
    if ((+this.empenhado + (this.entidade.id ? +this.entidade.valor_liquidado : 0) - +this.liquidado - +this.entidadeForm.get('valor_liquidado').value) < 0) {
      Mensagem.alerta(this.messageService, 'Não há saldo disponível para esta pré-liquidação!');
    } else {
      this.obterUltimaParcela();
    }
  }

  private async obterUltimaParcela() {
    this.preliquidacaoService.obterUltimaParcela(
      this.entidade.empenho.numero, this.entidade.empenho.exercicio.id, this.entidade.empenho.orgao.id
    ).pipe(takeUntil(this.unsubscribe))
      .subscribe(
        (res: any) => {
          this.entidade.parcela = res;
          if (+res === 0 &&
            +this.empenhado !== +this.entidadeForm.get('valor_liquidado').value) {
            this.entidadeForm.get('parcela').setValue(1);
          } else {
            if (!this.entidade.id) {
              this.entidadeForm.get('parcela').setValue(res);
            }
          }
        }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error })
      );
  }

  private async loadTotalizadores() {
    this.empenhado = 0;
    this.liquidado = 0;
    this.pago = 0;

    if (!this.entidade.empenho) {
      return;
    }

    let totalizadores = await this.empenhoService.totalizadores(
      this.login.exercicio.id, this.login.orgao.id, this.entidade.empenho.numero).toPromise();
    if (totalizadores) {
      totalizadores = totalizadores.content[0] ? totalizadores.content[0] : totalizadores.content;
      this.empenhado = totalizadores.total_empenhado;
      this.liquidado = totalizadores.total_liquidado;
      this.pago = totalizadores.total_pago;
    }

    if (!this.entidadeForm.get('id').value) {
      const saldo = (+this.empenhado - +this.liquidado);
      this.entidadeForm.get('valor_liquidado').setValue(saldo.toString());
      if (saldo <= 0) {
        Mensagem.alerta(this.messageService, 'Não há saldo disponível para esta pré-liquidação!');
      }
    }
  }

  private carregarAutoCompletes() {
    // autocomplete para empenho
    this.empenhoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('empenho'),
      this.empenhoService,
      'id', ['numero', 'exercicio.ano'], {
      orgao_id: this.login.orgao.id, exercicio_id: this.login.exercicio.id,
      relations: 'exercicio,orgao,favorecido,subelemento,ficha,ficha.acao,ficha.programa', orderBy: 'numero'
    }, { number: ['numero'] },
      () => {
        this.loadTotalizadores();
      }
    );
  }

  public async atualizarEmpenho() {
    if (this.empenhoNumero > 0 && !this.entidade.id) {
      const param = {};
      param['numero'] = this.empenhoNumero;
      param['exercicio.id'] = this.login.exercicio.id;
      param['orgao.id'] = this.login.orgao.id;
      param['relations'] = 'modalidade,ficha,ficha.acao,favorecido,favorecido.tipo,subelemento,'
        + 'exercicio,orgao,contrato,contrato.tipo_contratacao,ficha.despesa,ficha.funcao,'
        + 'ficha.subfuncao,ficha.programa,ficha.recurso,ficha.aplicacao,orgao,orgao.cidade,exercicio'

      this.entidade.empenho = await this.empenhoService.obter(param).pipe(takeUntil(this.unsubscribe)).toPromise();
      if (!this.entidade.empenho) {
        Mensagem.alerta(this.messageService, 'Empenho não localizado!');
        this.entidadeForm.get('empenho').setValue(null);
        this.loadTotalizadores();
        return;
      }

      this.entidade.orgao = this.entidade.empenho.orgao;
      this.entidade.exercicio = this.entidade.empenho.exercicio;
      if (this.entidade.empenho) {
        this.loadTotalizadores();
        this.obterUltimaParcela();
        if (!this.entidadeForm.get('data_liquidacao').value) {
          this.entidadeForm.get('data_liquidacao').setValue(new Date());
        }
        this.entidadeForm.get('empenho').setValue(this.entidade.empenho);
        this.entidadeForm.get('orgao').setValue(this.entidade.empenho.orgao);
        this.entidadeForm.get('exercicio').setValue(this.entidade.empenho.exercicio);
        this.entidadeForm.get('data_vencimento').setValue(new DateFormatPipe().transform(this.entidade.empenho.data_vencimento, []));
        this.entidadeForm.get('historico').setValue(this.entidade.empenho.historico);
        this.entidadeForm.get('mes').setValue(this.entidade.data_liquidacao.getUTCMonth() + 1);
      }
    }
  }

  public imprimir() {
    const parametros = {};

    let relations = '';
    relations += 'empenho.contrato,empenho.licitacao,empenho.modalidade,empenho.subelemento,empenho.ficha,';
    relations += 'empenho.favorecido,empenho.ficha.executora.unidade,empenho.ficha.despesa,empenho.ficha.funcao,';
    relations += 'empenho.ficha.acao,empenho.ficha.aplicacao';
    parametros['relations'] = relations;
    parametros['exercicio_id'] = this.login.exercicio.id;
    parametros['orgao_id'] = this.login.orgao.id;
    parametros['id'] = this.entidade.id;
    this.preliquidacaoService
      .extendido(1, -1, parametros)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(lista => {
        this.assinaturaService.obter()
        new NotaLiquidacao(this.assinaturaService).imprimir(lista.content, this.login);
      },
        (error) => this.messageService.add(
          { severity: 'error', summary: 'Atenção!', detail: error.error && error.error.payload ? error.error.payload : error }
        )
      );
  }

  sair() {
    if (this.redirecionar && this.podeIncluir('empenhos')) {
      this.router.navigate(['/empenhos']);
    } else {
      new FuncaoService().navegarPara(this.login.usuario.sistema, this.router);
    }
  }

  public voltar() {
    switch (this.login.sistema) {
      case 'contabil':
      case 'controle-interno':
        this.router.navigate(['/contabilidade']);
        break;
      default:
        this.sair();
        break;
    }
  }
}
